import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../../basic/Icon';
import styles from './DateRangePicker.module.css';
import {
  addMonths,
  formatRangeLabel,
  getMonthMatrix,
  isAfterDay,
  isBeforeDay,
  isSameDay,
  parseISODate,
  startOfMonth,
  toISODate,
} from './dateRange.utils';

export interface DateRange {
  /** Fecha inicial en formato ISO `YYYY-MM-DD` (o null). */
  from: string | null;
  /** Fecha final en formato ISO `YYYY-MM-DD` (o null). */
  to: string | null;
}

export interface DateRangePickerProps {
  value: DateRange;
  onChange: (value: DateRange) => void;
  /** Fecha mínima seleccionable (p. ej. hoy − 180 días). */
  minDate?: Date;
  /** Fecha máxima seleccionable (p. ej. hoy). */
  maxDate?: Date;
  /** Mes que se muestra al abrir si no hay valor. */
  defaultMonth?: Date;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
}

const WEEKDAYS = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'];
const MONTHS = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

export function DateRangePicker({
  value,
  onChange,
  minDate,
  maxDate,
  defaultMonth,
  placeholder = 'Fecha inicio y fin',
  disabled = false,
  className,
  id,
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);

  const initialView = value.from
    ? parseISODate(value.from)
    : value.to
      ? parseISODate(value.to)
      : defaultMonth ?? maxDate ?? new Date();
  const [view, setView] = useState<Date>(startOfMonth(initialView));

  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onDocMouseDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [isOpen]);

  const cells = useMemo(() => getMonthMatrix(view), [view]);

  const isDayDisabled = (day: Date): boolean =>
    (Boolean(minDate) && isBeforeDay(day, minDate as Date)) ||
    (Boolean(maxDate) && isAfterDay(day, maxDate as Date));

  const handleSelect = (day: Date) => {
    if (isDayDisabled(day)) return;
    const iso = toISODate(day);

    // Inicio de un rango nuevo (sin "from", o rango ya completo).
    if (!value.from || (value.from && value.to)) {
      onChange({ from: iso, to: null });
      return;
    }

    // Segundo click anterior al "from": reinicia el rango.
    if (isBeforeDay(day, parseISODate(value.from))) {
      onChange({ from: iso, to: null });
      return;
    }

    // Cierra el rango.
    onChange({ from: value.from, to: iso });
    setIsOpen(false);
  };

  const isSelected = (day: Date): boolean =>
    (Boolean(value.from) && isSameDay(day, parseISODate(value.from as string))) ||
    (Boolean(value.to) && isSameDay(day, parseISODate(value.to as string)));

  const isInRange = (day: Date): boolean =>
    Boolean(value.from && value.to) &&
    !isBeforeDay(day, parseISODate(value.from as string)) &&
    !isAfterDay(day, parseISODate(value.to as string));

  const label = formatRangeLabel(value.from, value.to);
  const canGoPrev = !minDate || isAfterDay(startOfMonth(view), startOfMonth(minDate));
  const canGoNext = !maxDate || isBeforeDay(startOfMonth(view), startOfMonth(maxDate));

  const rootClasses = [styles.root, className].filter(Boolean).join(' ');

  return (
    <div className={rootClasses} ref={rootRef}>
      <button
        type="button"
        id={id}
        data-testid="date-range-trigger"
        className={styles.trigger}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className={styles.icon}>
          <Icon name="calendar" size="sm" color="inherit" />
        </span>
        <span className={label ? styles.valueText : styles.placeholder}>{label || placeholder}</span>
      </button>

      {isOpen && (
        <div className={styles.popover} role="dialog" aria-label="Seleccionar rango de fechas">
          <div className={styles.header}>
            <button
              type="button"
              className={styles.nav}
              disabled={!canGoPrev}
              aria-label="Mes anterior"
              onClick={() => setView((prev) => addMonths(prev, -1))}
            >
              <Icon name="chevron-left" size="sm" color="inherit" />
            </button>
            <span className={styles.monthLabel}>
              {MONTHS[view.getMonth()]} {view.getFullYear()}
            </span>
            <button
              type="button"
              className={styles.nav}
              disabled={!canGoNext}
              aria-label="Mes siguiente"
              onClick={() => setView((prev) => addMonths(prev, 1))}
            >
              <Icon name="chevron-right" size="sm" color="inherit" />
            </button>
          </div>

          <div className={styles.grid}>
            {WEEKDAYS.map((weekday, index) => (
              <span key={`${weekday}-${index}`} className={styles.weekday}>
                {weekday}
              </span>
            ))}

            {cells.map((cell) => {
              const isOutsideMonth = cell.getMonth() !== view.getMonth();
              const dayDisabled = isDayDisabled(cell);
              const selected = isSelected(cell);
              const ranged = isInRange(cell);
              const dayClasses = [
                styles.day,
                isOutsideMonth ? styles.outside : '',
                ranged ? styles.inRange : '',
                selected ? styles.selected : '',
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <button
                  key={toISODate(cell)}
                  type="button"
                  data-date={toISODate(cell)}
                  className={dayClasses}
                  disabled={dayDisabled}
                  aria-pressed={selected}
                  onClick={() => handleSelect(cell)}
                >
                  {cell.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

DateRangePicker.displayName = 'DateRangePicker';
